import {
  initializeApp,
  getApps,
  getApp,
  FirebaseApp as FBApp,
} from 'firebase/app';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import {
  setPersistence,
  getAuth,
  browserLocalPersistence,
  Auth,
  connectAuthEmulator,
} from 'firebase/auth';
import {
  getFunctions,
  Functions,
  connectFunctionsEmulator,
} from 'firebase/functions';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { getMessaging, Messaging, getToken } from 'firebase/messaging';
import {
  connectDatabaseEmulator,
  Database,
  getDatabase,
} from 'firebase/database';
import {
  connectFirestoreEmulator,
  Firestore,
  getFirestore,
  initializeFirestore,
} from 'firebase/firestore';
import {
  getMoralisAuth,
  MoralisAuth,
} from '@moralisweb3/client-firebase-auth-utils';

export const FIREBASE_CONFIG = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

class FirebaseApp {
  app: FBApp;
  auth: Auth;
  firestore: Firestore;
  functions: Functions;
  messaging?: Messaging;
  database: Database;
  storage: FirebaseStorage;
  analytics?: Analytics;
  moralisAuth: MoralisAuth;

  public constructor() {
    if (!getApps().length) {
      this.app = initializeApp(FIREBASE_CONFIG);
      initializeFirestore(this.app, { ignoreUndefinedProperties: true });
    } else {
      this.app = getApp();
    }

    this.auth = getAuth(this.app);
    this.firestore = getFirestore(this.app);
    this.functions = getFunctions(this.app);
    this.database = getDatabase(this.app);
    this.storage = getStorage(this.app);
    this.moralisAuth = getMoralisAuth(this.app, { functions: this.functions });
    if (typeof window !== 'undefined') {
      this.analytics = getAnalytics(this.app);
      this.messaging = getMessaging(this.app);
      this.initToken();
    }

    this.setupEmulators();

    setPersistence(this.auth, browserLocalPersistence);
  }

  private initToken() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    getToken(this.messaging!, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
    })
      .then((currentToken) => {
        if (currentToken) {
          localStorage.setItem('messagingToken', currentToken);
          //should also set this in server
          return currentToken;
        }
        // Show permission request UI
        console.warn(
          'No registration token available. Request permission to generate one.',
        );
        return null;
      })
      .catch((err) => {
        console.error('An error occurred while retrieving token. ', err);
        // ...
      });
  }

  private setupEmulators() {
    if (process.env.NEXT_PUBLIC_EMULATOR_MODE === 'true') {
      connectFunctionsEmulator(this.functions, 'localhost', 5001);
      connectAuthEmulator(this.auth, 'http://localhost:9099');
      connectFirestoreEmulator(this.firestore, 'localhost', 8080);
      connectDatabaseEmulator(this.database, 'localhost', 9000);
    }
  }
}

const {
  app,
  auth,
  firestore,
  functions,
  messaging,
  database,
  storage,
  analytics,
  moralisAuth,
} = new FirebaseApp();

export {
  app as firebase,
  auth as firebaseAuth,
  firestore,
  moralisAuth,
  functions as firebaseFunctions,
  messaging,
  database as realtimeDb,
  storage,
  analytics,
};
